import * as React from 'react';
import * as moment from 'moment';
import * as _ from 'lodash';
import { useState } from 'react';
import withClickOutside from '../../../hoc/withClickOutside';
import { CalendarHeader } from './CalendarHeader';
import { AnalyticsChartPeriods } from '../../../services/mini/analytics/analyticsChartPeriods';
import { getTimeUnits } from '../../../services/mini/getTimeUnits';

type DatePickerPopupProps = {
  startDate: number,
  endDate: number,
  onChange: any,
  startFromDate: number,
  period: AnalyticsChartPeriods,
  onOutsideClick: () => void
};

function DatePickerPopup({ startDate, endDate, onChange, startFromDate, period, onOutsideClick }: DatePickerPopupProps) {
  const [date, setDate] = useState(moment(+endDate > Date.now() ? Date.now() : endDate));
  const [clickedStartDate, setClickedStartDate] = useState(startDate);
  const [clickedEndDate, setClickedEndDate] = useState(endDate);
  const [possibleEndDate, setPossibleEndDate] = useState(null);
  let showPrevControl;
  let showNextControl;
  let todayBtnLabel;
  let timeUnits;
  let step;
  let headerFormat;
  let formatValueFn;
  const dayNames = [];
  let todayBtnClass;

  if (period === AnalyticsChartPeriods.Daily) {
    todayBtnClass = 'day';
    headerFormat = 'MMMM YYYY';
    step = 'month';
    todayBtnLabel = 'Today';
    timeUnits = getTimeUnits(period, date, dayNames);
    timeUnits = _.flatten(timeUnits);
    formatValueFn = (_start) => {
      return _start.format('D');
    };
  }
  if (period === AnalyticsChartPeriods.Weekly) {
    todayBtnClass = 'week';
    headerFormat = 'MMMM YYYY';
    step = 'month';
    todayBtnLabel = 'This week';
    timeUnits = getTimeUnits(period, date);
    formatValueFn = (_start, _end) => {
      const isSame = _start.month() === _end.month();
      return isSame ? `${_start.format('MMM D')} - ${_end.format('D')}` : `${_start.format('MMM D')} - ${_end.format('MMM D')}`;

    };
  }
  if (period === AnalyticsChartPeriods.Monthly) {
    todayBtnClass = 'month';
    headerFormat = 'YYYY';
    step = 'year';
    todayBtnLabel = 'This month';
    timeUnits = getTimeUnits(period, date);
    formatValueFn = (_start) => {
      return _start.format('MMMM');
    };
  }

  const firstUnit = moment(timeUnits[0].start);
  const lastUnit = moment(timeUnits[timeUnits.length - 1].end);

  if (period === AnalyticsChartPeriods.Daily) {
    showPrevControl = +firstUnit > +startFromDate;
    showNextControl = !(moment(date).month() === moment().month() && moment(date).year() === moment().year());
  } else if (period === AnalyticsChartPeriods.Weekly) {
    showPrevControl = +firstUnit > +startFromDate;
    showNextControl = !(moment(date).month() === moment().month() && moment(date).year() === moment().year());
  } else if (period === AnalyticsChartPeriods.Monthly) {
    showPrevControl = +firstUnit > +startFromDate;
    showNextControl = +lastUnit < +moment().endOf('year');
  }


  return (
    <div className="date-picker-popup">
      <CalendarHeader
        title={moment(date).format(headerFormat)}
        goPrev={showPrevControl ? () => {
          setDate(moment(date).add(-1, step));
        } : undefined}
        goNext={showNextControl ? () => {
          setDate(moment(date).add(1, step));
        } : undefined} />
      <div
        className={`date-picker-popup__today-btn ${todayBtnClass}`}
        onClick={() => {
          setDate(moment());
        }}>{todayBtnLabel}</div>
      <div className="date-picker-popup__body">
        {period === AnalyticsChartPeriods.Daily &&
          <div className="date-picker-popup__weekdays">
            {dayNames.map(day => {
              return <div key={day} className={day === 'Su' ? 'sunday' : ''}>{day}</div>;
            })}
          </div>}
        {timeUnits.map(timeUnit => {
          let className = '';
          const weekStartTs = +timeUnit.start;
          const weekEndTs = +timeUnit.end;
          const clickedStartTs = +clickedStartDate;
          const clickedEndTs = +clickedEndDate;

          if (weekStartTs > clickedStartTs && weekEndTs <= +possibleEndDate) {
            className += ' possible-range';
          }

          if (weekEndTs < +startFromDate || weekStartTs > Date.now()) {
            className += ' disabled';
          } else {
            if ((weekStartTs <= clickedStartTs && clickedStartTs <= weekEndTs) ||
              (clickedEndDate && weekStartTs > clickedStartTs && weekEndTs <= clickedEndTs)) {
              className += ' selected';
            }
          }

          if (period === AnalyticsChartPeriods.Daily) {
            className += ' day';
            if (moment(timeUnit.start).format('dd') === 'Su') {
              className += ' sunday';
            }
            if (moment(weekEndTs).format('DD-MM-YYYY') === moment().format('DD-MM-YYYY')) {
              className += ' current';
            }
          }

          if (period === AnalyticsChartPeriods.Weekly) {
            className += ' week';
            if (moment(weekEndTs).week() === moment().week()) {
              className += ' current';
            }
          }

          if (period === AnalyticsChartPeriods.Monthly) {
            className += ' month';
            if (moment(weekEndTs).month() === moment().month() && moment(weekEndTs).year() === moment().year()) {
              className += ' current';
            }
          }

          return <div
            key={timeUnit.start}
            className={`date-picker-popup__item ${className}`}
            onMouseEnter={() => {
              if (!clickedEndDate) {
                setPossibleEndDate(timeUnit.end);
              }
            }}
            onClick={() => {
              if (clickedStartDate && !clickedEndDate && +clickedStartDate < +timeUnit.end) {
                setClickedEndDate(timeUnit.end);
                setPossibleEndDate(null);
                onChange([+clickedStartDate, +timeUnit.end]);
                onOutsideClick();
              } else {
                setClickedStartDate(timeUnit.start);
                setClickedEndDate(null);
                setPossibleEndDate(null);
                onChange([+timeUnit.start, +timeUnit.end]);
              }
            }}>
            {formatValueFn(timeUnit.start, timeUnit.end)}
          </div>;
        })}
      </div>
    </div>
  );
}

export default withClickOutside(DatePickerPopup);
